/**
 * Localisation from day one (AGENTS.md), not a later phase.
 *
 * id-ID is the default because that is who the product is for; en-US exists so
 * the string table is exercised by more than one locale from the start. The
 * pseudo-locale is the third: it is what catches hard-coded English and layouts
 * that break the moment a translation is 40% longer, and it is generated rather
 * than written so it can never fall behind the source.
 */
export interface Strings {
  appName: string;
  tabDeck: string;
  tabQuiz: string;
  tabInteraction: string;
  tabNarration: string;
  tabPublish: string;

  narrationHeading: string;
  narrationUnsupported: string;
  narrationLocalOnly: string;
  narrationPortabilityHeading: string;
  narrationPortabilityBody: string;
  narrationExportBundle: string;
  narrationImportBundle: string;
  narrationExported: (count: number) => string;
  narrationImported: (count: number) => string;
  microphone: string;
  defaultMicrophone: string;
  micReloading: string;
  inputLevel: string;
  recordNarration: string;
  stopRecording: string;
  discardTake: string;
  savingTake: string;
  narrationSilent: string;
  noTakesYet: string;
  takeNumber: (n: number) => string;
  playTake: string;
  stopPlayback: string;
  useThisTake: string;
  activeTake: string;
  deleteTake: string;
  narrationTotal: string;
  narrationSilentSlides: string;
  narrationLocalBytes: string;
  ttsHeading: string;
  ttsCloudNotice: string;
  ttsApiKey: string;
  ttsSaveApiKey: string;
  ttsLoadVoices: string;
  ttsTextSource: string;
  ttsSourceNotes: string;
  ttsSourceScript: string;
  ttsSourceBody: string;
  ttsScript: string;
  ttsCopyNotes: string;
  ttsCourseVoice: string;
  ttsModel: string;
  ttsSpeed: string;
  ttsSlideVoice: string;
  ttsInheritedVoice: string;
  ttsStyleFilter: string;
  ttsAllStyles: string;
  ttsCharacters: (slide: number, deck: number) => string;
  ttsProblems: string;
  ttsStale: string;
  ttsSyntheticDisclosure: string;
  ttsReviewConsent: string;
  ttsConsentHeading: string;
  ttsConsentBody: (characters: number) => string;
  ttsConsentConfirm: string;
  ttsSynthesize: string;
  ttsCancel: string;
  ttsWorking: string;
  ttsNeedsDeck: string;
  ttsNoText: string;
  ttsTimingPreview: string;
  ttsLexicon: string;
  ttsLexiconWritten: string;
  ttsLexiconPronunciation: string;
  ttsLexiconAdd: string;
  ttsLexiconRemove: string;
  ttsLexiconExport: string;
  ttsLexiconImport: string;
  ttsLexiconSync: string;
  ttsLexiconNeedsSync: string;
  ttsLexiconSynced: string;
  ttsLexiconConsentHeading: string;
  ttsLexiconConsentBody: (entries: number) => string;
  ttsLexiconConsentConfirm: string;
  ttsBatchAll: string;
  ttsBatchStale: string;
  ttsBatchConsentHeading: string;
  ttsBatchConsentBody: (slides: number, characters: number) => string;
  ttsBatchProgress: (completed: number, total: number) => string;
  ttsBatchCancel: string;
  ttsBatchNoSlides: string;

  loading: string;
  notInPowerPoint: string;
  notInPowerPointBody: string;

  deckHeading: string;
  readDeck: string;
  rereadDeck: string;
  readingDeck: string;
  deckSlides: string;
  deckParts: string;
  deckSize: string;
  deckMedia: string;
  deckAnimated: string;
  deckElapsed: string;
  slideList: string;
  slideNumber: (n: number) => string;
  hasQuiz: string;

  quizHeading: string;
  noSlideSelected: string;
  addQuiz: string;
  removeQuiz: string;
  quizTitle: string;
  passingScore: string;
  attemptsAllowed: string;
  addQuestion: string;
  removeQuestion: string;
  questionType: string;
  questionStem: string;
  questionPoints: string;
  choiceText: string;
  markCorrect: string;
  addChoice: string;
  acceptedAnswers: string;
  acceptedAnswersHelp: string;
  numericAnswer: string;
  numericTolerance: string;
  feedbackCorrect: string;
  feedbackIncorrect: string;
  tags: string;
  tagsHelp: string;
  validationHeading: string;
  validationClean: string;

  interactionHeading: string;
  interactionChooseType: string;
  interactionType: string;
  interactionTitle: string;
  interactionDescription: string;
  removeInteraction: string;
  interactionNavigation: string;
  interactionShowPrevNext: string;
  interactionRequireSequential: string;
  interactionTrackViewed: string;
  interactionCompletion: string;
  interactionAllViewed: string;
  interactionNViewed: string;
  interactionNoCompletion: string;
  interactionThreshold: string;
  interactionTheme: string;
  interactionInheritTheme: string;
  interactionCustomTheme: string;
  interactionAccent: string;
  interactionSurface: string;
  interactionText: string;
  interactionItems: string;
  interactionUntitledItem: string;
  interactionAddItem: string;
  interactionRemoveItem: string;
  interactionMoveUp: string;
  interactionMoveDown: string;
  interactionItemLabel: string;
  interactionItemShortLabel: string;
  interactionItemBody: string;
  interactionItemLatex: string;
  interactionItemDate: string;
  interactionItemMediaKind: string;
  interactionItemMediaAsset: string;
  interactionItemNoMedia: string;
  interactionItemAltText: string;
  interactionItemCaption: string;
  interactionReadDeckForMedia: string;
  interactionBulkEntry: string;
  interactionBulkHelp: string;
  interactionApplyBulk: string;
  interactionImportCsv: string;
  interactionPreview: string;
  interactionTypeNames: Record<string, string>;
  interactionTypeDescriptions: Record<string, string>;
  interactionIssueNames: Record<string, string>;
  interactionMediaTypeNames: Record<string, string>;

  publishHeading: string;
  target: string;
  publishButton: string;
  publishing: string;
  downloadPackage: string;
  fidelityHeading: string;
  fidelityClean: string;
  blockingHeading: string;
  droppedHeading: string;
  estimatedSize: string;
  publishBlocked: string;

  saving: string;
  saved: string;
  saveFailed: string;
  savePending: string;
  saveNow: string;
  savedInDeckNote: string;
  rememberedSlides: string;
  quotaWarning: string;
  clobberWarning: string;

  questionTypeNames: Record<string, string>;
  targetNames: Record<string, string>;
  severityNames: Record<string, string>;
}

export const ID_ID: Strings = {
  appName: 'SlideForge',
  tabDeck: 'Presentasi',
  tabQuiz: 'Kuis',
  tabInteraction: 'Interaksi',
  tabNarration: 'Narasi',
  tabPublish: 'Terbitkan',

  narrationHeading: 'Narasi slide ini',
  narrationUnsupported:
    'Versi PowerPoint ini tidak dapat merekam audio. Rekaman bekerja di PowerPoint Windows, Mac, dan web.',
  narrationLocalOnly:
    'Saat layanan SlideForge lokal berjalan, audio dicadangkan di sana, tetapi tidak disimpan di dalam file PowerPoint.',
  narrationPortabilityHeading: 'Pindahkan audio bersama presentasi',
  narrationPortabilityBody:
    'Saat mengirim file PowerPoint ke komputer lain, ekspor juga paket audio pendamping. Impor paket itu di komputer tujuan sebelum menerbitkan.',
  narrationExportBundle: 'Ekspor paket audio',
  narrationImportBundle: 'Impor paket audio',
  narrationExported: (count) => `${count} berkas audio diekspor ke paket pendamping.`,
  narrationImported: (count) => `${count} berkas audio diimpor dan diverifikasi.`,
  microphone: 'Mikrofon',
  defaultMicrophone: 'Mikrofon bawaan',
  micReloading:
    'Izin mikrofon diberikan. SlideForge dimuat ulang sebentar, lalu tekan Mulai merekam lagi.',
  inputLevel: 'Level masukan',
  recordNarration: 'Mulai merekam',
  stopRecording: 'Berhenti',
  discardTake: 'Buang',
  savingTake: 'Menyimpan rekaman…',
  narrationSilent: 'Tidak ada suara yang terdeteksi. Periksa mikrofon Anda.',
  noTakesYet: 'Belum ada rekaman untuk slide ini.',
  takeNumber: (n: number) => `Rekaman ${n}`,
  playTake: 'Putar',
  stopPlayback: 'Hentikan',
  useThisTake: 'Pakai ini',
  activeTake: 'Dipakai',
  deleteTake: 'Hapus',
  narrationTotal: 'Total narasi',
  narrationSilentSlides: 'Slide tanpa narasi',
  narrationLocalBytes: 'Audio di komputer ini',
  ttsHeading: 'Suara sintetis ElevenLabs',
  ttsCloudNotice:
    'ElevenLabs adalah layanan cloud. Teks hanya dikirim setelah Anda menyetujui setiap operasi dan tujuannya disebutkan.',
  ttsApiKey: 'Kunci API ElevenLabs',
  ttsSaveApiKey: 'Simpan kunci terenkripsi',
  ttsLoadVoices: 'Muat daftar suara',
  ttsTextSource: 'Sumber teks',
  ttsSourceNotes: 'Catatan pembicara',
  ttsSourceScript: 'Naskah narasi',
  ttsSourceBody: 'Teks pada slide',
  ttsScript: 'Naskah narasi',
  ttsCopyNotes: 'Salin catatan ke naskah',
  ttsCourseVoice: 'Suara bawaan kursus',
  ttsModel: 'Model ElevenLabs',
  ttsSpeed: 'Kecepatan suara',
  ttsSlideVoice: 'Suara khusus slide',
  ttsInheritedVoice: 'Ikuti suara kursus',
  ttsStyleFilter: 'Filter gaya',
  ttsAllStyles: 'Semua gaya',
  ttsCharacters: (slide, deck) => `${slide} karakter pada slide · ${deck} pada presentasi`,
  ttsProblems: 'Periksa pelafalan',
  ttsStale: 'Narasi lama tidak lagi cocok dengan teks. Buat ulang sebelum menerbitkan.',
  ttsSyntheticDisclosure: 'Beritahu peserta bahwa narasi dibuat secara sintetis',
  ttsReviewConsent: 'Tinjau dan buat narasi',
  ttsConsentHeading: 'Kirim teks ke ElevenLabs?',
  ttsConsentBody: (characters) =>
    `${characters} karakter catatan/naskah akan dikirim ke ElevenLabs untuk membuat audio.`,
  ttsConsentConfirm: 'Saya setuju mengirim teks ini ke ElevenLabs untuk operasi ini saja.',
  ttsSynthesize: 'Setuju dan buat audio',
  ttsCancel: 'Batal',
  ttsWorking: 'ElevenLabs sedang membuat audioâ€¦',
  ttsNeedsDeck: 'Baca presentasi agar catatan pembicara dan teks slide tersedia.',
  ttsNoText: 'Sumber yang dipilih belum memiliki teks.',
  ttsTimingPreview: 'Penanda waktu kata',
  ttsLexicon: 'Kamus pelafalan kursus',
  ttsLexiconWritten: 'Teks tertulis',
  ttsLexiconPronunciation: 'Ucapkan sebagai',
  ttsLexiconAdd: 'Tambah pelafalan',
  ttsLexiconRemove: 'Hapus',
  ttsLexiconExport: 'Ekspor JSON',
  ttsLexiconImport: 'Impor JSON',
  ttsLexiconSync: 'Sinkronkan ke ElevenLabs',
  ttsLexiconNeedsSync: 'Sinkronkan kamus pelafalan sebelum membuat audio agar aturannya diterapkan.',
  ttsLexiconSynced: 'Kamus pelafalan telah disinkronkan ke ElevenLabs.',
  ttsLexiconConsentHeading: 'Kirim kamus pelafalan ke ElevenLabs?',
  ttsLexiconConsentBody: (entries) => `${entries} aturan pelafalan akan dikirim ke ElevenLabs.`,
  ttsLexiconConsentConfirm: 'Saya setuju mengirim kamus ini ke ElevenLabs hanya untuk operasi ini.',
  ttsBatchAll: 'Buat narasi seluruh presentasi',
  ttsBatchStale: 'Buat ulang narasi kedaluwarsa',
  ttsBatchConsentHeading: 'Kirim teks beberapa slide ke ElevenLabs?',
  ttsBatchConsentBody: (slides, characters) =>
    `${characters} karakter dari ${slides} slide akan dikirim ke ElevenLabs secara berurutan.`,
  ttsBatchProgress: (completed, total) => `${completed} dari ${total} slide selesai`,
  ttsBatchCancel: 'Batalkan batch',
  ttsBatchNoSlides: 'Tidak ada slide yang cocok untuk operasi ini.',

  loading: 'Memuat…',
  notInPowerPoint: 'SlideForge berjalan di dalam PowerPoint',
  notInPowerPointBody:
    'Buka SlideForge dari tab SlideForge di PowerPoint. Halaman ini hanya menampilkan pesan ini di luar PowerPoint.',

  deckHeading: 'Presentasi ini',
  readDeck: 'Baca presentasi',
  rereadDeck: 'Baca ulang',
  readingDeck: 'Membaca presentasi…',
  deckSlides: 'Slide',
  deckParts: 'Bagian file',
  deckSize: 'Ukuran',
  deckMedia: 'Media',
  deckAnimated: 'Slide beranimasi',
  deckElapsed: 'Waktu baca',
  slideList: 'Daftar slide',
  slideNumber: (n) => `Slide ${n}`,
  hasQuiz: 'berisi kuis',

  quizHeading: 'Kuis pada slide ini',
  noSlideSelected: 'Pilih slide lebih dulu di daftar slide.',
  addQuiz: 'Tambah kuis',
  removeQuiz: 'Hapus kuis',
  quizTitle: 'Judul kuis',
  passingScore: 'Nilai lulus (%)',
  attemptsAllowed: 'Jumlah percobaan',
  addQuestion: 'Tambah soal',
  removeQuestion: 'Hapus soal',
  questionType: 'Tipe soal',
  questionStem: 'Pertanyaan',
  questionPoints: 'Bobot',
  choiceText: 'Pilihan',
  markCorrect: 'Benar',
  addChoice: 'Tambah pilihan',
  acceptedAnswers: 'Jawaban yang diterima',
  acceptedAnswersHelp: 'Pisahkan dengan tanda titik koma.',
  numericAnswer: 'Jawaban angka',
  numericTolerance: 'Toleransi',
  feedbackCorrect: 'Umpan balik jika benar',
  feedbackIncorrect: 'Umpan balik jika salah',
  tags: 'CPMK',
  tagsHelp: 'Pisahkan dengan tanda titik koma. Dikirim ke LMS sebagai objectives.',
  validationHeading: 'Pemeriksaan',
  validationClean: 'Tidak ada masalah.',

  interactionHeading: 'Interaksi pada slide ini',
  interactionChooseType: 'Pilih pola interaksi. Semua pola memakai model data dan mesin penyelesaian yang sama.',
  interactionType: 'Tipe interaksi',
  interactionTitle: 'Judul interaksi',
  interactionDescription: 'Deskripsi',
  removeInteraction: 'Hapus interaksi',
  interactionNavigation: 'Navigasi item',
  interactionShowPrevNext: 'Tampilkan tombol sebelumnya/berikutnya',
  interactionRequireSequential: 'Wajib dilihat berurutan',
  interactionTrackViewed: 'Lacak item yang sudah dilihat',
  interactionCompletion: 'Aturan penyelesaian',
  interactionAllViewed: 'Semua item dilihat',
  interactionNViewed: 'Sejumlah item dilihat',
  interactionNoCompletion: 'Tidak ada syarat',
  interactionThreshold: 'Jumlah item',
  interactionTheme: 'Tema',
  interactionInheritTheme: 'Ikuti warna presentasi (bawaan)',
  interactionCustomTheme: 'Gunakan warna khusus',
  interactionAccent: 'Aksen',
  interactionSurface: 'Latar',
  interactionText: 'Teks',
  interactionItems: 'Item',
  interactionUntitledItem: 'Item tanpa judul',
  interactionAddItem: 'Tambah item',
  interactionRemoveItem: 'Hapus item',
  interactionMoveUp: 'Pindahkan ke atas',
  interactionMoveDown: 'Pindahkan ke bawah',
  interactionItemLabel: 'Nama item',
  interactionItemShortLabel: 'Label pendek',
  interactionItemBody: 'Isi',
  interactionItemLatex: 'Sumber LaTeX (opsional)',
  interactionItemDate: 'Tanggal atau tonggak',
  interactionItemMediaKind: 'Jenis media',
  interactionItemMediaAsset: 'Berkas media',
  interactionItemNoMedia: 'Tanpa media',
  interactionItemAltText: 'Teks alternatif',
  interactionItemCaption: 'Keterangan media',
  interactionReadDeckForMedia: 'Baca presentasi untuk memilih media tertanam.',
  interactionBulkEntry: 'Masukkan banyak item',
  interactionBulkHelp: 'Tempel tabel label/isi atau CSV. Kolom ketiga dipakai sebagai tanggal pada timeline.',
  interactionApplyBulk: 'Tambahkan baris',
  interactionImportCsv: 'Impor CSV',
  interactionPreview: 'Pratinjau langsung',
  interactionTypeNames: { tabs: 'Tab', accordion: 'Akordeon / FAQ', timeline: 'Linimasa' },
  interactionTypeDescriptions: {
    tabs: 'Topik ringkas dalam panel bertab.',
    accordion: 'Pertanyaan dan jawaban yang dapat dibuka.',
    timeline: 'Tonggak berurutan secara horizontal atau vertikal.',
  },
  interactionIssueNames: {
    'empty-title': 'Tambahkan judul interaksi.',
    'empty-items': 'Tambahkan setidaknya satu item.',
    'duplicate-item-id': 'Ada item dengan identitas ganda.',
    'empty-label': 'Setiap item harus memiliki label.',
    'empty-body': 'Setiap item harus memiliki isi atau rumus.',
    'missing-date': 'Setiap item linimasa harus memiliki tanggal atau tonggak.',
    'missing-media': 'Pilih berkas media untuk item ini.',
    'missing-alt-text': 'Tambahkan teks alternatif untuk media visual.',
    'threshold-out-of-range': 'Jumlah item penyelesaian melebihi jumlah item yang tersedia.',
  },
  interactionMediaTypeNames: { none: 'Tanpa media', image: 'Gambar', video: 'Video', audio: 'Audio' },

  publishHeading: 'Terbitkan kursus',
  target: 'Format',
  publishButton: 'Terbitkan',
  publishing: 'Menerbitkan…',
  downloadPackage: 'Unduh paket',
  fidelityHeading: 'Laporan kesetiaan',
  fidelityClean: 'Semua slide akan tampil apa adanya.',
  blockingHeading: 'Harus diperbaiki sebelum terbit',
  droppedHeading: 'Yang tidak dapat dipertahankan',
  estimatedSize: 'Perkiraan ukuran',
  publishBlocked: 'Perbaiki dulu masalah di atas.',

  saving: 'Menyimpan…',
  saved: 'Tersimpan',
  saveFailed: 'Gagal menyimpan',
  savePending: 'Belum tersimpan',
  saveNow: 'Simpan sekarang',
  savedInDeckNote:
    'Kuis, interaksi, dan narasi disimpan di dalam file PowerPoint ini. Tekan Ctrl+S di PowerPoint agar ikut tersimpan ke disk.',
  rememberedSlides:
    'Daftar slide ini diingat dari sesi sebelumnya. Tekan "Baca presentasi" untuk memperbaruinya.',
  quotaWarning: 'Data kursus hampir melebihi kapasitas file PowerPoint.',
  clobberWarning: 'Orang lain menyunting presentasi ini bersamaan.',

  questionTypeNames: {
    multipleChoice: 'Pilihan ganda',
    multipleResponse: 'Pilihan ganda (jawaban banyak)',
    trueFalse: 'Benar / Salah',
    fillInTheBlank: 'Isian singkat',
    numeric: 'Angka',
    matching: 'Menjodohkan',
  },
  targetNames: {
    scorm12: 'SCORM 1.2',
    scorm2004: 'SCORM 2004',
    xapi: 'xAPI (Tin Can)',
    cmi5: 'cmi5',
    html5: 'HTML5 (folder web)',
    'html5-single-file': 'HTML5 (satu berkas)',
  },
  severityNames: {
    info: 'Info',
    warning: 'Peringatan',
    error: 'Kesalahan',
    blocking: 'Penghalang',
  },
};

export const EN_US: Strings = {
  appName: 'SlideForge',
  tabDeck: 'Deck',
  tabQuiz: 'Quiz',
  tabInteraction: 'Interactions',
  tabNarration: 'Narration',
  tabPublish: 'Publish',

  narrationHeading: 'Narration for this slide',
  narrationUnsupported:
    'This version of PowerPoint cannot record audio. Recording works in PowerPoint on Windows, on Mac, and on the web.',
  narrationLocalOnly:
    'When the local SlideForge service is running, audio is backed up there, but it is not stored inside the PowerPoint file.',
  narrationPortabilityHeading: 'Move audio with the presentation',
  narrationPortabilityBody:
    'When sending the PowerPoint file to another computer, export its companion audio bundle too. Import that bundle on the destination computer before publishing.',
  narrationExportBundle: 'Export audio bundle',
  narrationImportBundle: 'Import audio bundle',
  narrationExported: (count) => `${count} audio files exported to the companion bundle.`,
  narrationImported: (count) => `${count} audio files imported and verified.`,
  microphone: 'Microphone',
  defaultMicrophone: 'Default microphone',
  micReloading:
    'Microphone access granted. SlideForge is reloading; start the recording again afterwards.',
  inputLevel: 'Input level',
  recordNarration: 'Start recording',
  stopRecording: 'Stop',
  discardTake: 'Discard',
  savingTake: 'Saving the recording…',
  narrationSilent: 'No sound is being picked up. Check your microphone.',
  noTakesYet: 'No recordings for this slide yet.',
  takeNumber: (n: number) => `Take ${n}`,
  playTake: 'Play',
  stopPlayback: 'Stop',
  useThisTake: 'Use this',
  activeTake: 'In use',
  deleteTake: 'Delete',
  narrationTotal: 'Total narration',
  narrationSilentSlides: 'Slides without narration',
  narrationLocalBytes: 'Audio on this computer',
  ttsHeading: 'ElevenLabs synthetic voice',
  ttsCloudNotice:
    'ElevenLabs is a cloud service. Text is sent only after you approve each operation and the destination is named.',
  ttsApiKey: 'ElevenLabs API key',
  ttsSaveApiKey: 'Store encrypted key',
  ttsLoadVoices: 'Load voices',
  ttsTextSource: 'Text source',
  ttsSourceNotes: 'Speaker notes',
  ttsSourceScript: 'Narration script',
  ttsSourceBody: 'Slide body text',
  ttsScript: 'Narration script',
  ttsCopyNotes: 'Copy notes to script',
  ttsCourseVoice: 'Course default voice',
  ttsModel: 'ElevenLabs model',
  ttsSpeed: 'Voice speed',
  ttsSlideVoice: 'Voice for this slide',
  ttsInheritedVoice: 'Inherit course voice',
  ttsStyleFilter: 'Style filter',
  ttsAllStyles: 'All styles',
  ttsCharacters: (slide, deck) => `${slide} characters on this slide · ${deck} in the deck`,
  ttsProblems: 'Check pronunciation',
  ttsStale: 'Existing narration no longer matches the text. Regenerate it before publishing.',
  ttsSyntheticDisclosure: 'Tell learners that narration is synthetic',
  ttsReviewConsent: 'Review and generate narration',
  ttsConsentHeading: 'Send text to ElevenLabs?',
  ttsConsentBody: (characters) =>
    `${characters} characters of notes/script will be sent to ElevenLabs to generate audio.`,
  ttsConsentConfirm: 'I agree to send this text to ElevenLabs for this operation only.',
  ttsSynthesize: 'Agree and generate audio',
  ttsCancel: 'Cancel',
  ttsWorking: 'ElevenLabs is generating audioâ€¦',
  ttsNeedsDeck: 'Read the presentation to make speaker notes and slide text available.',
  ttsNoText: 'The selected source has no text yet.',
  ttsTimingPreview: 'Word timing markers',
  ttsLexicon: 'Course pronunciation dictionary',
  ttsLexiconWritten: 'Written text',
  ttsLexiconPronunciation: 'Pronounce as',
  ttsLexiconAdd: 'Add pronunciation',
  ttsLexiconRemove: 'Remove',
  ttsLexiconExport: 'Export JSON',
  ttsLexiconImport: 'Import JSON',
  ttsLexiconSync: 'Sync to ElevenLabs',
  ttsLexiconNeedsSync: 'Sync the pronunciation dictionary before generating audio so its rules are applied.',
  ttsLexiconSynced: 'The pronunciation dictionary is synced to ElevenLabs.',
  ttsLexiconConsentHeading: 'Send pronunciation dictionary to ElevenLabs?',
  ttsLexiconConsentBody: (entries) => `${entries} pronunciation rules will be sent to ElevenLabs.`,
  ttsLexiconConsentConfirm: 'I agree to send this dictionary to ElevenLabs for this operation only.',
  ttsBatchAll: 'Generate the whole presentation',
  ttsBatchStale: 'Regenerate stale narration',
  ttsBatchConsentHeading: 'Send multiple slides to ElevenLabs?',
  ttsBatchConsentBody: (slides, characters) =>
    `${characters} characters from ${slides} slides will be sent to ElevenLabs sequentially.`,
  ttsBatchProgress: (completed, total) => `${completed} of ${total} slides completed`,
  ttsBatchCancel: 'Cancel batch',
  ttsBatchNoSlides: 'No slides match this operation.',

  loading: 'Loading…',
  notInPowerPoint: 'SlideForge runs inside PowerPoint',
  notInPowerPointBody:
    'Open SlideForge from the SlideForge tab in PowerPoint. Outside PowerPoint this page shows only this message.',

  deckHeading: 'This presentation',
  readDeck: 'Read presentation',
  rereadDeck: 'Read again',
  readingDeck: 'Reading presentation…',
  deckSlides: 'Slides',
  deckParts: 'File parts',
  deckSize: 'Size',
  deckMedia: 'Media',
  deckAnimated: 'Animated slides',
  deckElapsed: 'Read time',
  slideList: 'Slide list',
  slideNumber: (n) => `Slide ${n}`,
  hasQuiz: 'has a quiz',

  quizHeading: 'Quiz on this slide',
  noSlideSelected: 'Choose a slide in the slide list first.',
  addQuiz: 'Add a quiz',
  removeQuiz: 'Remove quiz',
  quizTitle: 'Quiz title',
  passingScore: 'Passing score (%)',
  attemptsAllowed: 'Attempts allowed',
  addQuestion: 'Add question',
  removeQuestion: 'Remove question',
  questionType: 'Question type',
  questionStem: 'Question',
  questionPoints: 'Points',
  choiceText: 'Choice',
  markCorrect: 'Correct',
  addChoice: 'Add choice',
  acceptedAnswers: 'Accepted answers',
  acceptedAnswersHelp: 'Separate with semicolons.',
  numericAnswer: 'Numeric answer',
  numericTolerance: 'Tolerance',
  feedbackCorrect: 'Feedback when correct',
  feedbackIncorrect: 'Feedback when incorrect',
  tags: 'Learning outcomes',
  tagsHelp: 'Separate with semicolons. Sent to the LMS as objectives.',
  validationHeading: 'Checks',
  validationClean: 'No problems found.',

  interactionHeading: 'Interaction on this slide',
  interactionChooseType: 'Choose an interaction pattern. Every pattern uses the same data and completion engine.',
  interactionType: 'Interaction type',
  interactionTitle: 'Interaction title',
  interactionDescription: 'Description',
  removeInteraction: 'Remove interaction',
  interactionNavigation: 'Item navigation',
  interactionShowPrevNext: 'Show previous/next controls',
  interactionRequireSequential: 'Require items in sequence',
  interactionTrackViewed: 'Track viewed items',
  interactionCompletion: 'Completion rule',
  interactionAllViewed: 'All items viewed',
  interactionNViewed: 'A number of items viewed',
  interactionNoCompletion: 'No requirement',
  interactionThreshold: 'Number of items',
  interactionTheme: 'Theme',
  interactionInheritTheme: 'Inherit presentation colours (default)',
  interactionCustomTheme: 'Use custom colours',
  interactionAccent: 'Accent',
  interactionSurface: 'Surface',
  interactionText: 'Text',
  interactionItems: 'Items',
  interactionUntitledItem: 'Untitled item',
  interactionAddItem: 'Add item',
  interactionRemoveItem: 'Remove item',
  interactionMoveUp: 'Move up',
  interactionMoveDown: 'Move down',
  interactionItemLabel: 'Label',
  interactionItemShortLabel: 'Short label',
  interactionItemBody: 'Body',
  interactionItemLatex: 'LaTeX source (optional)',
  interactionItemDate: 'Date or milestone',
  interactionItemMediaKind: 'Media type',
  interactionItemMediaAsset: 'Media file',
  interactionItemNoMedia: 'No media',
  interactionItemAltText: 'Alternative text',
  interactionItemCaption: 'Media caption',
  interactionReadDeckForMedia: 'Read the presentation to choose embedded media.',
  interactionBulkEntry: 'Bulk entry',
  interactionBulkHelp: 'Paste a label/body table or CSV. A third column becomes the timeline date.',
  interactionApplyBulk: 'Add rows',
  interactionImportCsv: 'Import CSV',
  interactionPreview: 'Live preview',
  interactionTypeNames: { tabs: 'Tabs', accordion: 'Accordion / FAQ', timeline: 'Timeline' },
  interactionTypeDescriptions: {
    tabs: 'Compact topics in a tabbed panel.',
    accordion: 'Expandable questions and answers.',
    timeline: 'Milestones in horizontal or vertical order.',
  },
  interactionIssueNames: {
    'empty-title': 'Add an interaction title.',
    'empty-items': 'Add at least one item.',
    'duplicate-item-id': 'Two items have the same identity.',
    'empty-label': 'Every item needs a label.',
    'empty-body': 'Every item needs body content or a formula.',
    'missing-date': 'Every timeline item needs a date or milestone.',
    'missing-media': 'Choose a media file for this item.',
    'missing-alt-text': 'Add alternative text for visual media.',
    'threshold-out-of-range': 'The completion threshold exceeds the available item count.',
  },
  interactionMediaTypeNames: { none: 'No media', image: 'Image', video: 'Video', audio: 'Audio' },

  publishHeading: 'Publish course',
  target: 'Format',
  publishButton: 'Publish',
  publishing: 'Publishing…',
  downloadPackage: 'Download package',
  fidelityHeading: 'Fidelity report',
  fidelityClean: 'Every slide will appear as authored.',
  blockingHeading: 'Must be fixed before publishing',
  droppedHeading: 'What could not be preserved',
  estimatedSize: 'Estimated size',
  publishBlocked: 'Fix the problems above first.',

  saving: 'Saving…',
  saved: 'Saved',
  saveFailed: 'Could not save',
  savePending: 'Not saved yet',
  saveNow: 'Save now',
  savedInDeckNote:
    'Quizzes, interactions, and narration are stored inside this PowerPoint file. Press Ctrl+S in PowerPoint to write them to disk.',
  rememberedSlides:
    'This slide list is remembered from last time. Press "Read presentation" to refresh it.',
  quotaWarning: 'Course data is close to the limit of what fits in the PowerPoint file.',
  clobberWarning: 'Someone else is editing this presentation at the same time.',

  questionTypeNames: {
    multipleChoice: 'Multiple choice',
    multipleResponse: 'Multiple response',
    trueFalse: 'True / False',
    fillInTheBlank: 'Fill in the blank',
    numeric: 'Numeric',
    matching: 'Matching',
  },
  targetNames: {
    scorm12: 'SCORM 1.2',
    scorm2004: 'SCORM 2004',
    xapi: 'xAPI (Tin Can)',
    cmi5: 'cmi5',
    html5: 'HTML5 (web folder)',
    'html5-single-file': 'HTML5 (single file)',
  },
  severityNames: {
    info: 'Info',
    warning: 'Warning',
    error: 'Error',
    blocking: 'Blocking',
  },
};

/**
 * The pseudo-locale, generated from `EN_US`.
 *
 * Accents make untranslated text obvious at a glance; the padding simulates the
 * ~40% growth typical of a real translation, which is what surfaces a layout
 * that only ever fitted English. Generated, never hand-maintained, so it cannot
 * drift out of date the way a fourth string table would.
 */
export function pseudoLocalise(source: Strings): Strings {
  return mapStrings(source, (value) => `[${accent(value)}${padding(value)}]`);
}

const ACCENT_MAP: Readonly<Record<string, string>> = {
  a: 'á', b: 'ƀ', c: 'ç', d: 'đ', e: 'é', f: 'ƒ', g: 'ğ', h: 'ĥ', i: 'í', j: 'ĵ',
  k: 'ķ', l: 'ľ', m: 'ɱ', n: 'ń', o: 'ó', p: 'ƥ', q: 'ʠ', r: 'ř', s: 'š', t: 'ť',
  u: 'ú', v: 'ṽ', w: 'ŵ', x: 'ẋ', y: 'ý', z: 'ž',
  A: 'Á', B: 'Ɓ', C: 'Ç', D: 'Đ', E: 'É', F: 'Ƒ', G: 'Ğ', H: 'Ĥ', I: 'Í', J: 'Ĵ',
  K: 'Ķ', L: 'Ľ', M: 'Ṁ', N: 'Ń', O: 'Ó', P: 'Ƥ', Q: 'Ǫ', R: 'Ř', S: 'Š', T: 'Ť',
  U: 'Ú', V: 'Ṽ', W: 'Ŵ', X: 'Ẋ', Y: 'Ý', Z: 'Ž',
};

function accent(value: string): string {
  return [...value].map((ch) => ACCENT_MAP[ch] ?? ch).join('');
}

function padding(value: string): string {
  const extra = Math.max(1, Math.round(value.length * 0.4));
  return ` ${'·'.repeat(extra)}`;
}

function mapStrings(source: Strings, transform: (value: string) => string): Strings {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(source)) {
    if (typeof value === 'string') {
      out[key] = transform(value);
    } else if (typeof value === 'function') {
      out[key] = (...args: unknown[]) => transform(String((value as (...a: unknown[]) => string)(...args)));
    } else if (value && typeof value === 'object') {
      out[key] = Object.fromEntries(
        Object.entries(value as Record<string, string>).map(([k, v]) => [k, transform(v)]),
      );
    }
  }
  return out as unknown as Strings;
}

export type LocaleTag = 'id-ID' | 'en-US' | 'qps-ploc';

export function stringsFor(locale: string): Strings {
  const tag = locale.toLowerCase();
  // Pseudo-locale is opt-in via ?locale=qps-ploc; it must never be a fallback.
  if (tag === 'qps-ploc') return pseudoLocalise(EN_US);
  if (tag.startsWith('en')) return EN_US;
  return ID_ID;
}

export const SUPPORTED_LOCALES: readonly LocaleTag[] = ['id-ID', 'en-US'];
